// plateOwnshipGate.ts — Pure helpers for the ownship-on-plate GPS gate.
//
// Extracted to a standalone module so vitest can import them without
// pulling in pdfjs-dist (which requires a browser DOM and errors in
// the Node.js test environment).

/** Minimum position change (metres) to trigger an ownship update on plate. */
export const OWNSHIP_PLATE_MIN_MOVE_M = 100;

/** Minimum heading change (degrees) to trigger an ownship update on plate. */
export const OWNSHIP_PLATE_MIN_HDG_DEG = 2;

/**
 * Returns true when the GPS fix has moved enough to warrant updating the
 * plate-overlay ownship symbol layer.
 *
 * Gate criteria (either triggers an update):
 *   - Position moved > OWNSHIP_PLATE_MIN_MOVE_M metres
 *   - Heading changed > OWNSHIP_PLATE_MIN_HDG_DEG degrees (wrap-aware)
 */
export function shouldUpdateOwnshipOnPlate(
  prevLat: number,
  prevLon: number,
  prevHdg: number,
  nextLat: number,
  nextLon: number,
  nextHdg: number,
): boolean {
  // Rough equirectangular distance (good enough for the ~100 m gate).
  const dLat = (nextLat - prevLat) * 111_320;
  const dLon = (nextLon - prevLon) * 111_320 * Math.cos((prevLat * Math.PI) / 180);
  const distM = Math.sqrt(dLat * dLat + dLon * dLon);
  if (distM >= OWNSHIP_PLATE_MIN_MOVE_M) return true;
  const hdgDiff = Math.abs(((nextHdg - prevHdg + 540) % 360) - 180);
  return hdgDiff >= OWNSHIP_PLATE_MIN_HDG_DEG;
}
